// One look for field assets everywhere: AR, radar, map and list (2026-10-02,
// after the Armitage St test where water and wastewater pipes were the same
// blue). Pipes are coloured by NETWORK - drinking water blue, stormwater
// green, wastewater red (field_ar.NETWORK_COLOURS on the server). Valves,
// hydrants, meters, fittings and lids take their network's colour and a
// distinct SYMBOL, so colour says "which network" and shape says "what".
import { h } from "./dom";
import { networkOf } from "./arMath";

export type Network = "potable" | "stormwater" | "wastewater" | "unknown";

export const NETWORK_COLOURS: Record<Network, string> = {
  potable: "#2563eb", stormwater: "#16a34a", wastewater: "#dc2626", unknown: "#718096",
};
export const NETWORK_LABELS: Record<Network, string> = {
  potable: "Drinking water", stormwater: "Stormwater", wastewater: "Wastewater", unknown: "Network not recorded",
};
export const TYPE_LABELS: Record<string, string> = {
  pipe: "Pipe", valve: "Valve", hydrant: "Hydrant", toby: "Toby / service valve", meter: "Water meter",
  fitting: "Fitting", backflow: "Backflow preventer", other: "Lid / chamber", job: "Live job",
  pump_station: "Pump station", reservoir: "Reservoir / tank",
};

/** A valve's normal status (field_assets / network_facilities): hollow = open,
 * dark = closed; "unknown" keeps the plain filled diamond. */
export type ValveStatus = "open" | "closed" | "unknown";
export const VALVE_STATUS_LABELS: Record<ValveStatus, string> = {
  open: "Valve - normally open", closed: "Valve - normally closed", unknown: "Valve - status not recorded",
};

export type Symbol = "line" | "diamond" | "triangle" | "square" | "circle-dot" | "hexagon" | "dot" | "ring" | "star" | "pump" | "tank";
const TYPE_SYMBOL: Record<string, Symbol> = {
  pipe: "line", valve: "diamond", hydrant: "triangle", toby: "square", meter: "circle-dot", backflow: "hexagon",
  fitting: "dot", other: "ring", job: "star", pump_station: "pump", reservoir: "tank",
};

export function symbolOf(assetType: string): Symbol {
  return TYPE_SYMBOL[assetType] ?? "ring";
}

interface Styled {
  asset_type: string;
  network?: string | null;
  water_type?: string | null;
}

export function assetNetwork(a: Styled): Network {
  const n = a.network as Network | undefined;
  if (n && n in NETWORK_COLOURS) return n;
  return networkOf(a.water_type);
}

export function assetColour(a: Styled): string {
  if (a.asset_type === "job") return "#d69e2e";
  return NETWORK_COLOURS[assetNetwork(a)];
}

export function hexA(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return `rgba(113,128,150,${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** Draw a symbol centred on (x, y), `r` = outer radius (px). White outline so it reads over video and maps.
 * `status` (valves only): "open" = hollow (white fill, network-coloured edge), "closed" = dark fill. */
export function drawSymbol(ctx: CanvasRenderingContext2D, sym: Symbol, x: number, y: number, r: number, colour: string, alpha = 1,
  status?: ValveStatus): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = colour;
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = Math.max(1.5, r / 4);
  ctx.beginPath();
  if (sym === "diamond" && (status === "open" || status === "closed")) {
    // white halo, then the network-coloured edge around a white (open) or dark (closed) body
    const d = () => {
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
    };
    d();
    ctx.lineWidth = Math.max(3, r / 2.2);
    ctx.strokeStyle = "#fff";
    ctx.stroke();
    d();
    ctx.fillStyle = status === "open" ? "#fff" : "#1a202c";
    ctx.fill();
    ctx.lineWidth = Math.max(2, r / 3.5);
    ctx.strokeStyle = colour;
    ctx.stroke();
    ctx.restore();
    return;
  }
  switch (sym) {
    case "pump":
      // a pump: coloured disc, white edge, white flow triangle
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.fillStyle = "#fff";
      ctx.moveTo(x - r * 0.35, y - r * 0.5);
      ctx.lineTo(x + r * 0.55, y);
      ctx.lineTo(x - r * 0.35, y + r * 0.5);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      return;
    case "tank": {
      // a reservoir / tank: a cylinder
      const w = r * 0.85;
      const e = r * 0.32;
      ctx.moveTo(x - w, y - r * 0.55);
      ctx.lineTo(x - w, y + r * 0.55);
      ctx.ellipse(x, y + r * 0.55, w, e, 0, Math.PI, 0, true);
      ctx.lineTo(x + w, y - r * 0.55);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(x, y - r * 0.55, w, e, 0, 0, Math.PI * 2);
      ctx.fillStyle = "#bfdbfe";
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }
    case "line":
      ctx.lineWidth = Math.max(3, r / 2);
      ctx.strokeStyle = colour;
      ctx.moveTo(x - r, y + r * 0.5);
      ctx.lineTo(x + r, y - r * 0.5);
      ctx.stroke();
      ctx.restore();
      return;
    case "diamond":
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r, y);
      ctx.closePath();
      break;
    case "triangle":
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r * 0.95, y + r * 0.75);
      ctx.lineTo(x - r * 0.95, y + r * 0.75);
      ctx.closePath();
      break;
    case "square":
      ctx.rect(x - r * 0.75, y - r * 0.75, r * 1.5, r * 1.5);
      break;
    case "hexagon":
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI / 3) * k + Math.PI / 6;
        if (k === 0) ctx.moveTo(x + r * Math.cos(a), y + r * Math.sin(a));
        else ctx.lineTo(x + r * Math.cos(a), y + r * Math.sin(a));
      }
      ctx.closePath();
      break;
    case "star":
      for (let k = 0; k < 10; k++) {
        const a = (Math.PI / 5) * k - Math.PI / 2;
        const rr = k % 2 ? r * 0.45 : r;
        if (k === 0) ctx.moveTo(x + rr * Math.cos(a), y + rr * Math.sin(a));
        else ctx.lineTo(x + rr * Math.cos(a), y + rr * Math.sin(a));
      }
      ctx.closePath();
      break;
    case "dot":
      ctx.arc(x, y, r * 0.6, 0, Math.PI * 2);
      break;
    case "ring":
      // a lid: thick coloured ring, white edges
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.fillStyle = "#fff";
      ctx.arc(x, y, r * 0.45, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    case "circle-dot":
    default:
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.fillStyle = "#fff";
      ctx.arc(x, y, r * 0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
  }
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/** A small inline symbol for lists and cards (canvas, so it matches the map/AR exactly). */
export function symbolIcon(a: Styled & { normal_status?: string | null }, size = 18): HTMLCanvasElement {
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const c = document.createElement("canvas");
  c.width = c.height = Math.round(size * dpr);
  c.style.width = c.style.height = `${size}px`;
  c.className = "fa-sym";
  const ctx = c.getContext("2d");
  if (ctx) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawSymbol(ctx, symbolOf(a.asset_type), size / 2, size / 2, size / 2 - 2, assetColour(a), 1,
      a.asset_type === "valve" ? (a.normal_status as ValveStatus | undefined) : undefined);
  }
  return c;
}

/** The legend: networks (pipes) and symbols (fittings). */
export function legendEl(compact = false): HTMLElement {
  const nets: Network[] = ["potable", "stormwater", "wastewater"];
  const types = ["valve", "hydrant", "toby", "meter", "fitting", "other"];
  return h("div", { class: `fa-legend${compact ? " compact" : ""}`, role: "note", "aria-label": "Legend" },
    h("div", { class: "fa-legend-row" },
      ...nets.map((n) => h("span", { class: "fa-legend-item" },
        h("span", { class: "fa-legend-line", style: `background:${NETWORK_COLOURS[n]}` }), NETWORK_LABELS[n]))),
    h("div", { class: "fa-legend-row" },
      ...types.map((t) => h("span", { class: "fa-legend-item" }, symbolIcon({ asset_type: t, network: "unknown" }, 14), TYPE_LABELS[t]))),
    facilityLegendRow(),
  );
}

/** Network facilities: valve normal status, pump stations, reservoirs / tanks. */
export function facilityLegendRow(): HTMLElement {
  return h("div", { class: "fa-legend-row" },
    h("span", { class: "fa-legend-item" }, symbolIcon({ asset_type: "valve", network: "potable", normal_status: "open" }, 14), "Valve open"),
    h("span", { class: "fa-legend-item" }, symbolIcon({ asset_type: "valve", network: "potable", normal_status: "closed" }, 14), "Valve closed"),
    h("span", { class: "fa-legend-item" }, symbolIcon({ asset_type: "pump_station", network: "wastewater" }, 14), TYPE_LABELS.pump_station),
    h("span", { class: "fa-legend-item" }, symbolIcon({ asset_type: "reservoir", network: "potable" }, 14), TYPE_LABELS.reservoir),
  );
}

/** Map icon id for a (type, network[, valve status]); see addMapIcons. */
export function mapIconId(assetType: string, network: string, status?: string | null): string {
  const net = network in NETWORK_COLOURS ? network : "unknown";
  if (assetType === "valve" && (status === "open" || status === "closed")) return `fa-diamond-${status}-${net}`;
  return `fa-${symbolOf(assetType)}-${net}`;
}

/** The field map's network facility toggles (Area layer, 2026-10-06). */
export const FACILITY_TOGGLES: { key: "valve" | "pump_station" | "reservoir"; label: string }[] = [
  { key: "valve", label: "Valves (open / closed)" },
  { key: "pump_station", label: "Pump stations" },
  { key: "reservoir", label: "Reservoirs / tanks" },
];

interface IconMap {
  hasImage: (id: string) => boolean;
  addImage: (id: string, img: { width: number; height: number; data: Uint8Array | Uint8ClampedArray }, opts?: { pixelRatio?: number }) => void;
}

/** Register every symbol x network as a map image (no glyphs needed, so it
 * works with the offline LINZ style too). Call on every style load. */
export function addMapIcons(map: IconMap): void {
  const px = 2;
  const size = 22 * px;
  for (const sym of Object.values(TYPE_SYMBOL)) {
    if (sym === "line") continue;
    for (const net of Object.keys(NETWORK_COLOURS) as Network[]) {
      const id = `fa-${sym}-${net}`;
      if (map.hasImage(id)) continue;
      const c = document.createElement("canvas");
      c.width = c.height = size;
      const ctx = c.getContext("2d");
      if (!ctx) continue;
      drawSymbol(ctx, sym, size / 2, size / 2, size / 2 - 3 * px, sym === "star" ? "#d69e2e" : NETWORK_COLOURS[net]);
      const img = ctx.getImageData(0, 0, size, size);
      map.addImage(id, { width: size, height: size, data: img.data }, { pixelRatio: px });
    }
  }
  // valve normal-status variants (hollow = open, dark = closed)
  for (const status of ["open", "closed"] as const) {
    for (const net of Object.keys(NETWORK_COLOURS) as Network[]) {
      const id = `fa-diamond-${status}-${net}`;
      if (map.hasImage(id)) continue;
      const c = document.createElement("canvas");
      c.width = c.height = size;
      const ctx = c.getContext("2d");
      if (!ctx) continue;
      drawSymbol(ctx, "diamond", size / 2, size / 2, size / 2 - 3 * px, NETWORK_COLOURS[net], 1, status);
      const img = ctx.getImageData(0, 0, size, size);
      map.addImage(id, { width: size, height: size, data: img.data }, { pixelRatio: px });
    }
  }
}
